import { installIntroCoverage } from './intro-coverage-r355.js';

// WHY [BOL-AUTH-CUBE-R345]: use the SAME published R344 world rig on one real
// auth anchor. The older standalone rig cannot deliver its violent impulseAt;
// current R294 world field supports rests:[] (rest hero, zero travel energy).
export function mountAuthCube(main: HTMLElement, anchor: HTMLElement, button: HTMLButtonElement) {
  const host = document.createElement('div');
  host.id = 'auth-sculpture';
  host.setAttribute('aria-hidden', 'true');
  host.innerHTML = '<div id="stage"><canvas id="canvas"></canvas><div class="intro-layer"><video id="intro" muted playsinline></video></div><div id="fallback" hidden></div><span id="hint" hidden></span></div>';
  document.body.append(host);
  const abort = new AbortController();
  let disposed = false, rig: any = null, rigReady = false, introFrameAvailable = false, introStarted = false, introFailed = false, cached: any = null;
  const intro = host.querySelector<HTMLVideoElement>('#intro')!;
  const layer = intro.parentElement as HTMLElement;
  let introTimeout: ReturnType<typeof setTimeout> | null = null;
  // WHY [BOL-AUTH-INTRO-FALLBACK-R360]: the logo image is React-owned, so a
  // class placed on `main` is erased on the sign-in/signup rerender. Keep
  // entrance ownership on this rig host. CSS hides the assembled image from
  // the first auth paint; reveal the film only after its frame-zero callback.
  const hideFallbackForIntro = () => {
    if (disposed || !main.isConnected || introFrameAvailable || intro.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !intro.videoWidth || !intro.videoHeight) return;
    introFrameAvailable = true;
    layer.hidden = false;
    host.classList.remove('auth-fallback-visible');
    host.classList.add('auth-intro-frame');
  };
  const restoreFallback = () => {
    introFrameAvailable = false;
    layer.hidden = true;
    host.classList.add('auth-fallback-visible');
    host.classList.remove('auth-intro-frame');
  };
  const useLiveFallback = () => {
    if (!rigReady) {
      if (introFailed || intro.error) restoreFallback();
      return; // keep the actual final film frame until the live rig is ready
    }
    layer.hidden = true;
    host.classList.remove('auth-intro-frame', 'auth-fallback-visible');
    host.classList.add('auth-rig-ready');
  };
  const startBufferedIntro = () => {
    if (disposed || introFailed || introStarted || !Number.isFinite(intro.duration) || intro.duration <= 0) return;
    const hasCompleteBuffer = Array.from({ length: intro.buffered.length }, (_, index) => index)
      .some(index => intro.buffered.start(index) <= 0.05 && intro.buffered.end(index) >= intro.duration - 0.05);
    if (!hasCompleteBuffer) return;
    if (!introFrameAvailable) return;
    introStarted = true;
    placeIntro();
    intro.currentTime = 0;
    intro.play().catch(() => {
      introStarted = false;
      introFailed = true;
      useLiveFallback();
      intro.dispatchEvent(new Event('earlyintrocancel'));
    });
  };
  const watchFirstPresentedFrame = () => {
    if (!intro.requestVideoFrameCallback) return;
    intro.requestVideoFrameCallback((_now, meta) => {
      // WHY R355: a decoded frame arriving after timeout must not reopen a
      // stopped film over the live/static fallback. Failure is terminal.
      if (disposed || introFailed) return;
    if (!introFrameAvailable && !intro.seeking && Math.abs(meta.mediaTime) <= 0.001) {
        hideFallbackForIntro();
        if (introTimeout) clearTimeout(introTimeout);
        introTimeout = null;
        if (introFrameAvailable && new URLSearchParams(location.search).has('authRigProof')) {
          const rect = intro.getBoundingClientRect();
          (window as any).__authIntroFirstPresented = { mediaTime: meta.mediaTime, videoTime: intro.currentTime, rigReady, rect: [rect.x, rect.y, rect.width, rect.height], pixels: [intro.videoWidth, intro.videoHeight] };
        }
        startBufferedIntro();
      }
      if (!introFrameAvailable && !intro.ended) watchFirstPresentedFrame();
    });
  };
  intro.preload = 'auto';
  intro.src = new URL('/media/ops-boot-r355-intro.mp4', location.origin).href;
  intro.addEventListener('loadeddata', startBufferedIntro, { signal: abort.signal });
  intro.addEventListener('loadedmetadata', startBufferedIntro, { signal: abort.signal });
  intro.addEventListener('progress', startBufferedIntro, { signal: abort.signal });
  intro.addEventListener('canplaythrough', startBufferedIntro, { signal: abort.signal });
  intro.addEventListener('error', () => { introFailed = true; if (introTimeout) clearTimeout(introTimeout); introTimeout = null; useLiveFallback(); }, { signal: abort.signal });
  intro.addEventListener('ended', useLiveFallback, { signal: abort.signal });
  let press: { id: number; x: number; y: number; scrollY: number } | null = null;
  let dragged = false, mouseFired = false;
  const viewportHeight = () => Math.max(innerHeight, document.documentElement.clientHeight);
  const readLayout = () => {
    if (cached) return cached;
    const box = anchor.getBoundingClientRect();
    const image = button.querySelector('img')?.getBoundingClientRect();
    const size = Math.max(1, Math.min(image?.width || box.width, image?.height || box.height));
    return cached = {
      hero: { id: 'auth', x: box.left + box.width / 2, y: box.top + box.height / 2 + scrollY, size },
      rests: [], obstacles: [], gaps: [], width: innerWidth,
      contentWidth: document.documentElement.clientWidth, height: viewportHeight(), ring: 'auto'
    };
  };
  const refresh = () => { cached = null; host.style.height = viewportHeight() + 'px'; rig?.anchors?.refresh(); };
  const placeIntro = () => {
    const { hero } = readLayout(), size = hero.size;
    if (!(size > 0)) return;
    // WHY R360: placement is available from the stable auth anchor before
    // asynchronous rig readiness. Match the approved mediaFrame exactly.
    Object.assign(layer.style, {
      position: 'absolute', left: `${hero.x - 32 / 15 * size}px`, top: `${hero.y - scrollY - 1.8 * size}px`,
      width: `${64 / 15 * size}px`, height: `${3.6 * size}px`, pointerEvents: 'none'
    });
    Object.assign(intro.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block' });
  };
  const updatePlacement = () => { cached = null; placeIntro(); refresh(); };
  addEventListener('scroll', updatePlacement, { signal: abort.signal, passive: true });
  placeIntro();
  layer.hidden = false;
  introTimeout = setTimeout(() => {
    if (disposed || introFrameAvailable || introFailed) return;
    introFailed = true;
    useLiveFallback();
    intro.dispatchEvent(new Event('earlyintrocancel'));
  }, 8000);
  watchFirstPresentedFrame(); // register before paint/load so frame zero is observed
  installIntroCoverage(intro, {
    signal: abort.signal,
    readAnchor: () => {
      const layout = readLayout();
      return { ...layout.hero, y: layout.hero.y - scrollY, width: layout.width, height: layout.height };
    }
  });
  // WHY [BOL-AUTH-REPLAY-R347]: the assembled logo button is fixed, while
  // live burst fragments leave it. Raycast the actual clicked fragment through
  // the same world impulse API and scroll/drag/control guards as landing R222.
  // WHY R357: match landing R352: desktop press fires immediately at force2.
  // Suppress its compatibility click; touch still waits to distinguish a swipe.
  // The auth logo button is allowed, but form controls and links stay protected.
  const blockedTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element) || button.contains(target)) return false;
    return !!target.closest('a,button,input,select,textarea,summary,label,video,audio,iframe,[role="button"],[role="link"],[contenteditable]:not([contenteditable="false"])');
  };
  const keyboardActivate = (event: MouseEvent) => {
    if (event.detail !== 0 || !rig || disposed) return;
    const box = button.getBoundingClientRect();
    rig.impulseAt?.(box.left + box.width / 2, box.top + box.height / 2, matchMedia("(pointer: fine)").matches ? 2 : 1);
  };
  button.addEventListener('click', keyboardActivate, { signal: abort.signal });
  addEventListener('pointerdown', event => {
    press = { id: event.pointerId, x: event.clientX, y: event.clientY, scrollY };
    dragged = false; mouseFired = false;
    if (event.pointerType === 'mouse' && event.button === 0 && !event.defaultPrevented && !blockedTarget(event.target) && rig && !disposed) {
      mouseFired = !!rig.impulseAt?.(event.clientX, event.clientY, 2);
    }
  }, { signal: abort.signal, passive: true, capture: true });
  addEventListener('pointermove', event => {
    if (press && event.pointerId === press.id && Math.hypot(event.clientX - press.x, event.clientY - press.y) >= 6) dragged = true;
  }, { signal: abort.signal, passive: true, capture: true });
  addEventListener('pointerup', event => {
    if (press && event.pointerId === press.id && Math.hypot(event.clientX - press.x, event.clientY - press.y) >= 6) dragged = true;
  }, { signal: abort.signal, passive: true, capture: true });
  addEventListener('pointercancel', () => { press = null; dragged = true; }, { signal: abort.signal, passive: true, capture: true });
  addEventListener('click', event => {
    if (mouseFired) { mouseFired = false; press = null; dragged = false; return; }
    const wasDragged = dragged || (press && Math.abs(scrollY - press.scrollY) >= 6);
    press = null;
    dragged = false;
    if (!rig || disposed || event.defaultPrevented || event.detail === 0 || event.button !== 0 || wasDragged) return;
    if (blockedTarget(event.target)) return;
    rig.impulseAt?.(event.clientX, event.clientY, 1);
  }, { signal: abort.signal });
  addEventListener('resize', updatePlacement, { signal: abort.signal });
  // WHY R355: the brand's 780ms account-glass-arrive transform changes
  // screen position/scale without firing ResizeObserver. A cached early pose
  // left the held film 12px below the later live cube on a slow rig startup.
  // Remeasure at that animation boundary and on font/size changes.
  const brand = anchor.closest('.login-simple__brand');
  for (const name of ['animationend', 'animationcancel']) {
    brand?.addEventListener(name, event => { if (event.target === brand) updatePlacement(); }, { signal: abort.signal });
  }
  const observer = new ResizeObserver(updatePlacement);
  observer.observe(anchor);
  document.fonts.ready.then(() => { if (!disposed) updatePlacement(); });
  host.style.height = viewportHeight() + 'px';
  import(/* @vite-ignore */ '/cube/cube-runtime-r377.js').then(({ mountCube }) => {
    if (disposed) return;
    rig = mountCube(host, {
      assetBase: new URL('/cube/', location.origin),
      introSrc: intro.src,
      playIntro: true,
      adoptIntro: true,
      world: { readLayout }
    });
    if (new URLSearchParams(location.search).has("authRigProof")) (window as any).__authCubeRig = rig;
    // Readiness controls only visual fallback ownership, never auth UI rendering.
    rig.ready.then(() => {
      if (disposed) return;
      if (rig.error) { restoreFallback(); return; }
      rigReady = true;
      if (introFailed || intro.error || intro.ended) useLiveFallback();
      refresh();
    }, () => { if (!disposed) restoreFallback(); });
  }).catch(error => {
    if (!disposed) { restoreFallback(); console.error('Business Ops cube unavailable', error); }
  });
  return () => {
    disposed = true;
    if (introTimeout) clearTimeout(introTimeout);
    abort.abort();
    observer.disconnect();
    rig?.dispose();
    host.remove();
  };
}
